import styled from 'styled-components';
import { Form, Button, Card } from 'antd';

export const Container = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background.primary};
  padding: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

export const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

export const PageTitle = styled.h1`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

export const PageSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.md};
  margin: 0;
`;

export const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  
  @media (max-width: 768px) {
    gap: ${({ theme }) => theme.spacing.md};
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

export const StepItem = styled.div<{ $active?: boolean; $completed?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  position: relative;
  opacity: ${({ $active }) => ($active ? 1 : 0.5)};
  transition: all 0.3s ease;
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    top: 18px;
    left: calc(100% + 12px);
    width: 40px;
    height: 2px;
    background: ${({ theme, $completed }) =>
    $completed ? theme.colors.trading.buy : theme.colors.border.primary};
    
    @media (max-width: 768px) {
      width: 20px;
    }
  }
`;

export const StepNumber = styled.div<{ $active?: boolean; $completed?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.md};
  background: ${({ theme, $active, $completed }) => {
    if ($completed) return theme.colors.trading.buy;
    if ($active) return 'rgba(14, 203, 129, 0.2)';
    return theme.colors.background.tertiary;
  }};
  color: ${({ theme, $active, $completed }) => {
    if ($completed || $active) return theme.colors.trading.buy;
    return theme.colors.text.secondary;
  }};
  border: 2px solid ${({ theme, $active, $completed }) => {
    if ($completed || $active) return theme.colors.trading.buy;
    return theme.colors.border.primary;
  }};
  transition: all 0.3s ease;
`;

export const StepLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 500;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.fontSize.xs};
  }
`;

export const FormContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

export const FormCard = styled(Card)`
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  
  .ant-card-body {
    padding: ${({ theme }) => theme.spacing.xl};
  }
  
  @media (max-width: 768px) {
    .ant-card-body {
      padding: ${({ theme }) => theme.spacing.md};
    }
  }
`;

export const FormSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  display: flex;
  align-items: center;
`;

export const SectionDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FormItem = styled(Form.Item)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  
  .ant-form-item-label > label {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: 500;
    font-size: ${({ theme }) => theme.fontSize.sm};
  }
  
  .ant-input,
  .ant-input-number,
  .ant-select-selector,
  .ant-picker,
  textarea {
    color: ${({ theme }) => theme.colors.text.primary} !important;
    
    &:hover {
      border-color: ${({ theme }) => theme.colors.primary} !important;
    }
    
    &:focus,
    &.ant-input-focused,
    &.ant-select-focused .ant-select-selector {
      border-color: ${({ theme }) => theme.colors.primary} !important;
      box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.1) !important;
    }
  }
  
  .ant-input-number-handler-wrap {
    background: ${({ theme }) => theme.colors.background.tertiary};
    border-left-color: ${({ theme }) => theme.colors.border.primary};
  }
  
  .ant-input-number-handler {
    border-color: ${({ theme }) => theme.colors.border.primary};
    
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
  
  .ant-radio-button-wrapper {
    background: ${({ theme }) => theme.colors.background.tertiary};
    border-color: ${({ theme }) => theme.colors.border.primary};
    color: ${({ theme }) => theme.colors.text.secondary};
    
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
    
    &.ant-radio-button-wrapper-checked {
      background: ${({ theme }) => theme.colors.primary};
      border-color: ${({ theme }) => theme.colors.primary};
      color: ${({ theme }) => theme.colors.background.primary};
    }
  }
  
  .ant-select-arrow,
  .ant-input-number-handler-up-inner,
  .ant-input-number-handler-down-inner {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  
  .ant-select-selection-placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
  
  textarea {
    resize: vertical;
    min-height: 80px;
  }
`;

export const InfoBox = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.md};
  background: rgba(252, 213, 53, 0.1);
  border: 1px solid rgba(252, 213, 53, 0.3);
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    
    button {
      width: 100%;
    }
  }
`;

export const SubmitButton = styled(Button)`
  background: ${({ theme }) => theme.colors.trading.buy} !important;
  border-color: ${({ theme }) => theme.colors.trading.buy} !important;
  color: ${({ theme }) => theme.colors.background.primary} !important;
  font-weight: 600;
  height: 44px;
  padding: 0 ${({ theme }) => theme.spacing.xl};
  
  &:hover:not(:disabled) {
    background: #0db870 !important;
    border-color: #0db870 !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(14, 203, 129, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
  }
  
  transition: all 0.2s ease;
`;

export const CancelButton = styled(Button)`
  background: transparent !important;
  border: 1px solid ${({ theme }) => theme.colors.border.primary} !important;
  color: ${({ theme }) => theme.colors.text.secondary} !important;
  font-weight: 600;
  height: 44px;
  padding: 0 ${({ theme }) => theme.spacing.xl};
  
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.border.secondary} !important;
    color: ${({ theme }) => theme.colors.text.primary} !important;
  }
  
  transition: all 0.2s ease;
`;

export const SuccessCard = styled.div`
  max-width: 600px;
  margin: 100px auto;
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
`;

export const IconWrapper = styled.div`
  animation: scaleIn 0.5s ease-in-out;
  
  @keyframes scaleIn {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

